import { useEffect, useState } from 'react';

import * as wikiApi from '@/features/wiki/api';
import { WikiPageRef } from '@/features/wiki/types';
import { wikiPagePath } from '@/constants/routes';

const DocIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-accent">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);
const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-fg-faint">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14L21 3" />
  </svg>
);

/**
 * The item drawer's "Wiki pages" section: wiki pages this item is related to — DERIVED (not editable):
 * `source: 'item'` = this item's description links the page ("links to"); `source: 'wiki'` = the page
 * embeds this item ("mentioned in"). Refreshes when `refreshKey` changes (e.g. after a description save).
 */
export default function ItemWikiPagesSection({ itemId, refreshKey }: { itemId: string; refreshKey?: unknown }) {
  const [pages, setPages] = useState<WikiPageRef[]>([]);
  useEffect(() => {
    let cancelled = false;
    wikiApi
      .listItemWikiPages(itemId)
      .then((p) => !cancelled && setPages(p))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [itemId, refreshKey]);

  if (!pages.length) return null;

  return (
    <section className="mt-6">
      <div className="mb-2 flex items-baseline gap-2 border-b border-line pb-2 text-[13px] font-semibold text-fg-strong">
        Wiki pages <span className="text-fg-faint">· {pages.length}</span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {pages.map((p) => (
          <li key={`${p.page_id}-${p.source}`}>
            <a
              href={wikiPagePath(p.space_slug, p.slug)}
              target="_blank"
              rel="noreferrer noopener"
              title={`${p.space_name} / ${p.title || 'Untitled'}`}
              className="group flex items-center gap-2 rounded-lg border border-line bg-surface-raised px-2.5 py-1.5 no-underline transition-colors hover:border-line-strong hover:bg-layer"
            >
              {p.icon ? <span className="shrink-0 text-[14px] leading-none">{p.icon}</span> : <DocIcon />}
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg-soft">{p.title || 'Untitled'}</span>
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">{p.source === 'wiki' ? 'mentioned in' : 'links to'}</span>
              <ExternalIcon />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
